import polyfill from "../../utils/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "../../utils/analytics";
import { getDefaultAppState } from "../../appState";
import { ErrorDialog } from "../../components/board/ErrorDialog";
import { TopErrorBoundary } from "../../components/board/TopErrorBoundary";
import { notification } from 'antd';
import {
  APP_NAME,
  EVENT,
  VERSION_TIMEOUT,
} from "../../utils/constants";
import { loadFromBlob } from "../../data/blob";
import type {
  FileId,
  NonDeletedSchemadrawElement,
  OrderedSchemadrawElement,
} from "../../element/types";
import { useCallbackRefState } from "../../hooks/useCallbackRefState";
import { t } from "../../utils/i18n";
import {
  Schemadraw,
} from "../../common/index";
import {
  StoreAction,
} from "../../utils/store"
import {
  reconcileElements
} from "../../data/reconcile"
import type {
  AppState,
  SchemadrawImperativeAPI,
  BinaryFiles,
  SchemadrawInitialDataState,
  UIAppState,
} from "../../utils/types";
import type { ResolvablePromise } from "../../utils/utils";
import {
  debounce,
  getVersion,
  getFrame,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
} from "../../utils/utils";
import {
  TXCOS_STORAGE_PREFIXES,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "../../utils/constants";
import Collab, {
  CollabAPI,
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./Collab/Collab";
import {
  createToBackend,
  shareToBackend,
  getCollaborationLinkData,
  isCollaborationLink,
  loadScene,
} from "../../data";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "../../data/localStorage";
import CustomStats from "../../data/CustomStats";
import type { RestoredDataState } from "../../data/restore";
import { restore, restoreAppState } from "../../data/restore";
import { updateStaleImageStatuses } from "../../data/FileManager";
import { newElementWith } from "../../element/mutateElement";
import { isInitializedImageElement } from "../../element/typeChecks";
import { loadFilesFromTxcos } from "../../data/txcos";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
} from "../../data/LocalData";
import { isBrowserStorageStateNewer } from "../../data/tabSync";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "../../data/library";
import { AppMainMenu } from "../../components/board/AppMainMenu";
import { AppWelcomeScreen } from "../../components/board/AppWelcomeScreen";
import { atom, Provider, useAtom, useAtomValue } from "jotai";
import { useAtomWithInitialValue } from "../../utils/jotai";
import { jotaiStore } from "../../utils/jotai";

import "./index.scss";
import type { ResolutionType } from "../../utils/utility-types";
import { ShareableLinkDialog } from "../../components/board/ShareableLinkDialog";
import { openConfirmModal } from "../../components/board/OverwriteConfirm/OverwriteConfirmState";
import { OverwriteConfirmDialog } from "../../components/board/OverwriteConfirm/OverwriteConfirm";
import Trans from "../../components/board/Trans";
import { ShareDialog, shareDialogStateAtom } from "../../components/board/ShareDialog";
import type { RemoteSchemadrawElement } from "../../data/reconcile";
import { appThemeAtom, useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import { AIComponents } from "../../components/board/AI";


polyfill();

window.SCHEMADRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const relogonConfirmDialog = {
  title: t("relogonConfirm.title"),
  description: (
    <Trans
      i18nKey="relogonConfirm.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("relogonConfirm.button"),
  color: "warning",
} as const;


const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  schemadrawAPI: SchemadrawImperativeAPI;
}): Promise<
  { scene: SchemadrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const privateKey = searchParams.get("key");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: RestoredDataState & {
    scrollToContent?: boolean;
  } = await loadScene(null, null, localDataState);

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);

  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData || id ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        scene = await loadScene(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
          localDataState,
        );
      } else if (id) {
        scene = await loadScene(
          id,
          privateKey,
          localDataState
        );
      }
      scene.scrollToContent = true;
    } else {
      // https://github.com/schemadraw/schemadraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.pathname);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.pathname);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  } else {
    scene = await loadScene(
      id,
      privateKey,
      localDataState
    );
    scene.scrollToContent = true;
    return { scene: scene, isExternalScene };
  }

  if (roomLinkData && opts.collabAPI) {
    const { schemadrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            schemadrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
          id: roomLinkData.roomId,
        },
        elements: reconcileElements(
          scene?.elements || [],
          schemadrawAPI.getSceneElementsIncludingDeleted() as RemoteSchemadrawElement[],
          schemadrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    if (isExternalScene)
      if (id) {
        return {
          scene,
          isExternalScene,
          id: id ? id : "",
          key: scene.appState.privateKey ? scene.appState.privateKey : "",
        }
      } else if (jsonBackendMatch) {
        return {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      } else {
        return { scene, isExternalScene: false }
      }
  }
  return { scene: null, isExternalScene: false };
};

const SchemadrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const [appTheme, setAppTheme] = useAtom(appThemeAtom);
  const { editorTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<SchemadrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<SchemadrawInitialDataState | null>();
  }

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [schemadrawAPI, schemadrawRefCallback] =
    useCallbackRefState<SchemadrawImperativeAPI>();

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });

  useHandleLibrary({
    schemadrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  useEffect(() => {
    if (!schemadrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromAlioss({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              schemadrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                schemadrawAPI,
                erroredFiles,
                elements: schemadrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromTxcos(
            `${TXCOS_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            schemadrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              schemadrawAPI,
              erroredFiles,
              elements: schemadrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  schemadrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  schemadrawAPI,
                  erroredFiles,
                  elements: schemadrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    initializeScene({ collabAPI, schemadrawAPI }).then(async (data) => {
      window.document.title = "元纹 | " + (data.scene?.appState?.name ? data.scene?.appState?.name : "无标题 (请保存)");
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        schemadrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, schemadrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            schemadrawAPI.updateScene({
              ...data.scene,
              ...restore(data.scene, null, null, { repairBindings: true }),
              storeAction: StoreAction.CAPTURE,
            });
          }
        });
      }
    };
    const syncData = debounce(() => {
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          schemadrawAPI.updateScene({
            ...localDataState,
            storeAction: StoreAction.UPDATE,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              schemadrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = schemadrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = schemadrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  schemadrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  schemadrawAPI,
                  erroredFiles,
                  elements: schemadrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, schemadrawAPI, setLangCode]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        schemadrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          schemadrawAPI.getSceneElements(),
        )
      ) {
        preventUnload(event);
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [schemadrawAPI]);

  const onChange = (
    elements: readonly OrderedSchemadrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }
    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (schemadrawAPI) {
          let didChange = false;

          const elements = schemadrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            schemadrawAPI.updateScene({
              elements,
              storeAction: StoreAction.UPDATE,
            });
          }
        }
      });
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedSchemadrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      return window.alert(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { id, md5, errorMessage } = await createToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (md5) {
        schemadrawAPI!.updateScene({ appState: { contentMd5: md5 } });
        notification["success"]({ message: '保存成功', description: '回到设计管理页面，可以看到所有文件。', });
        window.open("/draw?id=" + id, "_self");

      }

      if (errorMessage !== null && errorMessage !== "") {
        if (errorMessage === t("relogonConfirm.description")) {
          if (await openConfirmModal(relogonConfirmDialog)) {
            window.open("/login?from=" + window.encodeURIComponent(document.URL), "_self");
            return
          }
        }

        console.log(errorMessage);
        notification["warning"]({ message: '保存失败', description: errorMessage, });
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, { width, height });
        setErrorMessage(error.message);
      }
    }
  };

  const onShareToBackend = async (
    exportedElements: readonly NonDeletedSchemadrawElement[],
    appState: AppState,
    files: BinaryFiles,
    canvas: HTMLCanvasElement | null,
  ) => {
    if (exportedElements.length === 0) {
      return window.alert(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, md5, errorMessage } = await shareToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (md5) {
        schemadrawAPI!.updateScene({ appState: { contentMd5: md5 } });
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        setErrorMessage(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedSchemadrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => schemadrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("schemadraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Schemadraw
        schemadrawAPI={schemadrawRefCallback}
        onChange={onChange}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
              onShareToBackend,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
      >
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        {schemadrawAPI && <AIComponents excalidrawAPI={schemadrawAPI} />}

        {isCollaborating && isOffline && (
          <div className="collab-offline-warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {schemadrawAPI && !isCollabDisabled && (
          <Collab schemadrawAPI={schemadrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (schemadrawAPI) {
              try {
                await onExportToBackend(
                  schemadrawAPI.getSceneElements(),
                  schemadrawAPI.getAppState(),
                  schemadrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}
      </Schemadraw>
    </div>
  );
};

const SchemadrawApp = () => {
  return (
    <TopErrorBoundary>
      <Provider unstable_createStore={() => jotaiStore}>
        <SchemadrawWrapper />
      </Provider>
    </TopErrorBoundary>
  );
};

export default SchemadrawApp;
